import React, { useState, useEffect, useCallback, useRef } from 'react';
import CloudAuthService from './cloudAuthService';
import { toast } from 'react-hot-toast';
import { ProviderMark, humanizeSsoError } from './providerIcons';
import { PENDING_DOWNLOAD_KEY } from '../../../utils/downloadResume';

/**
 * Flagship config-driven SSO: brand marks from Agent GET /providers.
 * No IdP client secrets in the browser.
 *
 * @param {'login'|'register'} mode — copy only; OAuth start is identical
 * @param {'first'|'last'} layout — SSO above or below email
 * @param {'compact'|'list'} density — modal-friendly grid vs full-width list
 * @param {'download'|null} afterAuthIntent
 * @param {() => void|Promise<void>} onBeforeNavigate
 */
export default function CloudAuthButtons({
  mode = 'login',
  onError,
  disabled = false,
  layout = 'first',
  density = 'compact',
  afterAuthIntent = null,
  onBeforeNavigate = null,
}) {
  const [loadingProvider, setLoadingProvider] = useState(null);
  const [providers, setProviders] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [retryNonce, setRetryNonce] = useState(0);
  const autoRetried = useRef(false);

  const loadProviders = useCallback(async (signal) => {
    setLoadingList(true);
    setLoadError(null);
    try {
      const list = await CloudAuthService.listEnabledOAuthProviders();
      if (signal?.aborted) return;
      setProviders(Array.isArray(list) ? list : []);
      setLoadError(null);
      autoRetried.current = false;
    } catch (err) {
      if (signal?.aborted) return;
      setProviders([]);
      setLoadError(humanizeSsoError(err?.message || 'provider_unavailable'));
      if (!autoRetried.current) {
        const msg = String(err.message || err || '');
        const apiDown = /502|406|405|failed to fetch|networkerror|api_unreachable/i.test(msg);
        if (!apiDown) {
          autoRetried.current = true;
          window.setTimeout(() => {
            if (!signal?.aborted) setRetryNonce((n) => n + 1);
          }, 1200);
        }
      }
    } finally {
      if (!signal?.aborted) setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    const ac = typeof AbortController !== 'undefined' ? new AbortController() : null;
    loadProviders(ac?.signal);
    return () => {
      ac?.abort();
    };
  }, [loadProviders, retryNonce]);

  const stampAfterAuthIntent = () => {
    if (afterAuthIntent !== 'download') return;
    try {
      const prev = (() => {
        try {
          const raw = sessionStorage.getItem(PENDING_DOWNLOAD_KEY);
          return raw ? JSON.parse(raw) : {};
        } catch {
          return {};
        }
      })();
      sessionStorage.setItem(
        PENDING_DOWNLOAD_KEY,
        JSON.stringify({
          ...prev,
          at: Date.now(),
          source: 'sso',
          mode: mode === 'register' ? 'register' : 'signin',
          flow: 'download',
        })
      );
    } catch {
      /* sessionStorage blocked */
    }
  };

  const handleCloudLogin = async (providerId) => {
    if (disabled || loadingProvider) return;
    const id = String(providerId || '').toLowerCase();
    if (!/^[a-z][a-z0-9_-]{0,31}$/.test(id)) {
      toast.error(humanizeSsoError('invalid_request'));
      return;
    }
    setLoadingProvider(id);
    try {
      stampAfterAuthIntent();
      if (typeof onBeforeNavigate === 'function') {
        await onBeforeNavigate(id, mode);
      }
      await CloudAuthService.initiateCloudAuth(id, mode);
    } catch (err) {
      setLoadingProvider(null);
      const msg = humanizeSsoError(err.message || `Failed to connect with ${id}`);
      toast.error(msg);
      if (onError) onError(err);
    }
  };

  const actionText = mode === 'register' ? 'Sign up' : 'Continue';
  const isBusy = disabled || !!loadingProvider;
  const isCompact = density !== 'list';

  if (loadingList) {
    return (
      <div className="w-full mb-1" aria-busy="true" aria-live="polite">
        <div className="h-3 w-28 rounded bg-slate-100 animate-pulse mb-3" />
        <div
          className={
            isCompact
              ? 'grid grid-cols-3 gap-2'
              : 'grid grid-cols-1 sm:grid-cols-2 gap-2'
          }
        >
          {[0, 1, 2, 3, 4, 5].slice(0, isCompact ? 6 : 2).map((i) => (
            <div
              key={i}
              className={
                isCompact
                  ? 'h-[4.25rem] rounded-xl bg-slate-100 animate-pulse'
                  : 'h-12 rounded-xl bg-slate-100 animate-pulse'
              }
            />
          ))}
        </div>
        {layout === 'first' && (
          <div className="relative flex py-3 items-center" role="presentation">
            <div className="flex-grow border-t border-slate-100" />
          </div>
        )}
      </div>
    );
  }

  if (loadError && !providers.length) {
    return (
      <div className="w-full mb-3 rounded-xl border border-amber-200/80 bg-amber-50/90 px-3 py-2.5 text-center">
        <p className="text-xs font-medium text-amber-900/90">
          Social sign-in temporarily unavailable
        </p>
        <p className="mt-0.5 text-[11px] text-amber-800/70">
          {loadError || 'Use email and password, or retry.'}
        </p>
        <button
          type="button"
          onClick={() => {
            autoRetried.current = true;
            setRetryNonce((n) => n + 1);
          }}
          className="mt-1.5 text-[11px] font-semibold text-amber-900 underline underline-offset-2 hover:text-amber-950"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!providers.length) {
    return null;
  }

  const heading =
    mode === 'register' ? 'Sign up with' : 'Continue with';

  const compactGrid =
    providers.length <= 3
      ? 'grid grid-cols-3 gap-2'
      : providers.length === 4
        ? 'grid grid-cols-4 gap-2'
        : 'grid grid-cols-5 gap-2';

  const providersBlock = (
    <div className="w-full">
      <div className="mb-2.5 flex items-baseline justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-slate-500">
          {heading}
        </p>
        <p className="text-[10px] text-slate-400 hidden sm:block shrink-0">
          Candor SSO
        </p>
      </div>

      {isCompact ? (
        <div className={compactGrid} role="group" aria-label="Identity providers">
          {providers.map((provider) => {
            const isLoading = loadingProvider === provider.id;
            const label = provider.display_name || provider.id;
            const id = String(provider.id || '').toLowerCase();

            return (
              <button
                key={provider.id}
                type="button"
                disabled={isBusy}
                onClick={() => handleCloudLogin(provider.id)}
                className={[
                  'group relative flex flex-col items-center justify-center gap-1.5',
                  'min-h-[4.25rem] px-1.5 py-2.5',
                  'rounded-xl border border-slate-200/90 bg-white text-slate-800',
                  'shadow-[0_1px_1px_rgba(15,23,42,0.03)]',
                  'transition-all duration-150 ease-out',
                  'hover:border-slate-300 hover:bg-slate-50 hover:shadow-sm hover:-translate-y-px',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/12 focus-visible:ring-offset-1',
                  'disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none',
                  isLoading ? 'ring-2 ring-slate-900/10 border-slate-300' : '',
                ].join(' ')}
                title={`${actionText} with ${label}`}
                aria-label={`${actionText} with ${label}`}
                aria-busy={isLoading}
              >
                {isLoading ? (
                  <span className="w-5 h-5 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin" />
                ) : (
                  <>
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-50 border border-slate-100/90 group-hover:bg-white transition-colors">
                      <ProviderMark id={id} className="w-[18px] h-[18px]" />
                    </span>
                    <span className="text-[11px] font-semibold tracking-tight text-slate-700 leading-none truncate max-w-full px-0.5">
                      {label}
                    </span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div
          className={
            providers.length === 1
              ? 'grid grid-cols-1 gap-2'
              : 'grid grid-cols-1 sm:grid-cols-2 gap-2'
          }
          role="group"
          aria-label="Identity providers"
        >
          {providers.map((provider) => {
            const isLoading = loadingProvider === provider.id;
            const label = provider.display_name || provider.id;
            const id = String(provider.id || '').toLowerCase();

            return (
              <button
                key={provider.id}
                type="button"
                disabled={isBusy}
                onClick={() => handleCloudLogin(provider.id)}
                className={[
                  'group relative flex items-center gap-3 w-full min-h-[44px] px-3.5 py-2.5',
                  'rounded-xl border border-slate-200/90 bg-white text-slate-800',
                  'text-sm font-semibold tracking-tight',
                  'shadow-[0_1px_1px_rgba(15,23,42,0.03)]',
                  'transition-all duration-150',
                  'hover:border-slate-300 hover:bg-slate-50',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-900/12 focus-visible:ring-offset-1',
                  'disabled:opacity-45 disabled:cursor-not-allowed',
                ].join(' ')}
                title={`${actionText} with ${label}`}
                aria-label={`${actionText} with ${label}`}
                aria-busy={isLoading}
              >
                {isLoading ? (
                  <span className="w-5 h-5 border-2 border-slate-300 border-t-slate-700 rounded-full animate-spin mx-auto" />
                ) : (
                  <>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 border border-slate-100">
                      <ProviderMark id={id} className="w-[18px] h-[18px]" />
                    </span>
                    <span className="flex-1 text-left truncate">{label}</span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const divider = (
    <div className="relative flex py-3 items-center justify-center" role="separator">
      <div className="flex-grow border-t border-slate-200" />
      <span className="flex-shrink mx-2.5 text-[10px] uppercase font-semibold text-slate-400 tracking-[0.12em]">
        or email
      </span>
      <div className="flex-grow border-t border-slate-200" />
    </div>
  );

  if (layout === 'first') {
    return (
      <div className="w-full mb-0.5">
        {providersBlock}
        {divider}
      </div>
    );
  }

  return (
    <div className="w-full mt-0.5">
      {divider}
      {providersBlock}
    </div>
  );
}
